import { useMemo } from 'react';
import { ApolloError } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useObserver } from 'mobx-react-lite';

import { useGetLoyaltyPassUrlQuery } from 'types/graphql';

import useMediaQuery from 'shared/hooks/use-media-query';
import { mediaQueries } from 'shared/styles';
import { isIOS } from 'shared/helpers/utils';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useIsFullstackDispensary } from 'src/hooks/use-is-fullstack-dispensary';
import { detectLoyaltyIntegration, Integrations } from 'src/components/loyalty/use-loyalty';
import useUser from 'src/hooks/use-user';
import { useDutchiePay } from 'src/payments/hooks';
import { useGetRewards } from 'src/components/loyalty/use-get-rewards';
import { getLogger } from 'src/utils/datadog-logger';

const logger = getLogger('loyalty-pass');

type UseLoyaltyPassUrlParams = {
  requirePayByBankEnrollment?: boolean;
};

type UseLoyaltyPassReturn = {
  loyaltyPassUrl: string | null;
  loyaltyPassShortUrl: string | null;
  loyaltyPassUrlLoading: boolean;
  loyaltyPassUrlError: ApolloError | undefined;
};

/**
 * Hook for detecting user eligibility and fetching the loyalty pass URL.
 *
 * This hook handles the complete flow for determining if a user can create a loyalty pass:
 * 1. Checks feature flag and device compatibility
 * 2. Fetches user's loyalty rewards data
 * 3. Validates loyalty account status
 * 4. Fetches loyalty pass URL if all conditions are met
 *
 * Eligibility requirements:
 * - Feature flag `ecomm.loyalty-pass-ctas.rollout` must be enabled
 * - User must have a valid loyalty account (dutchiepos, alpineiq, or springbig)
 * - Dispensary must be a fullstack dispensary
 * - User must be on a compatible device (desktop or iOS mobile)
 * - If `requirePayByBankEnrollment` is true, user must be enrolled in Dutchie Pay
 *
 * Device compatibility:
 * - ✓ Desktop (all platforms)
 * - ✓ Mobile iOS (iPhone/iPad)
 * - ✗ Mobile Android/other
 *
 * Skip logic optimization:
 * The hook skips fetching rewards data if any eligibility requirement cannot be met,
 * avoiding unnecessary API calls. This includes checking device compatibility and
 * Dutchie Pay enrollment (when required) before making network requests.
 *
 * @param params - Configuration options
 * @returns Loyalty pass URL, loading state, and any errors
 *
 * @example
 * // In bank account view (requires Dutchie Pay enrollment)
 * const { loyaltyPassUrl, loyaltyPassUrlLoading } = useLoyaltyPassUrl({
 *   requirePayByBankEnrollment: true
 * });
 */
export const useLoyaltyPassUrl = ({
  requirePayByBankEnrollment = false,
}: UseLoyaltyPassUrlParams): UseLoyaltyPassReturn => {
  const User = useUser();
  const flags = useFlags();
  const { dispensary } = useDispensary();
  const isFullstackDispensary = useIsFullstackDispensary();
  const { isEnrolledDutchiePay } = useDutchiePay();
  // Device Compatibility
  const isIpadorSmaller = useMediaQuery(mediaQueries.largeTablet);
  const userIsIOS = useMemo(() => isIOS(), []);

  const phone = useObserver(() => User.phone);
  const birthday = useObserver(() => User.birthday);

  const loyaltyProgram = detectLoyaltyIntegration(dispensary);
  const customerId = User?.id;
  const pin = User.getLoyaltyPin(dispensary?.id);

  const isShowLoyaltyPassFlagEnabled = flags['ecomm.loyalty-pass-ctas.rollout'] ?? false;

  const skipGetRewards =
    !isShowLoyaltyPassFlagEnabled ||
    (loyaltyProgram === Integrations.alpineiq && !pin) ||
    !customerId ||
    !birthday ||
    !phone ||
    (isIpadorSmaller && !userIsIOS) ||
    (requirePayByBankEnrollment && !isEnrolledDutchiePay) ||
    !dispensary?.id ||
    !isFullstackDispensary ||
    loyaltyProgram === Integrations.none;

  const { data: rewardsData, loading: rewardsLoading } = useGetRewards({
    dispensaryId: dispensary?.id,
    birthday,
    pin,
    phone,
    skip: skipGetRewards,
    customerId,
  });

  const hasValidLoyaltyAccount =
    (loyaltyProgram === Integrations.dutchiepos && !!rewardsData?.getRewardsV2?.isLoyaltyMember) ||
    (loyaltyProgram === Integrations.alpineiq && !!rewardsData?.getRewardsV2?.userHasWallet) ||
    (loyaltyProgram === Integrations.springbig && !!rewardsData?.getRewardsV2?.userHasWallet);

  // User must be eligible and have valid loyalty account
  const canCreatePass =
    !!rewardsData &&
    isFullstackDispensary &&
    hasValidLoyaltyAccount &&
    (!requirePayByBankEnrollment || isEnrolledDutchiePay);

  // Skip URL query if rewards were skipped OR user can't create pass
  const skipLoyaltyPassUrlQuery = skipGetRewards || !canCreatePass;

  const {
    data: loyaltyPassUrlData,
    loading: loyaltyPassUrlLoading,
    error: loyaltyPassUrlError,
  } = useGetLoyaltyPassUrlQuery({
    variables: {
      dispensaryId: dispensary?.id,
    },
    skip: skipLoyaltyPassUrlQuery,
    onError(error) {
      logger.error('Failed to get loyalty pass URL', {
        dispensaryId: dispensary?.id,
        error,
      });
    },
  });

  const shortUrl = loyaltyPassUrlData?.getLoyaltyPassUrl?.shortUrl;
  const url = loyaltyPassUrlData?.getLoyaltyPassUrl?.url;

  const loyaltyPassUrl = skipLoyaltyPassUrlQuery ? null : url ?? null;
  const loyaltyPassShortUrl = skipLoyaltyPassUrlQuery ? null : shortUrl ?? url ?? null;

  return {
    loyaltyPassUrl,
    loyaltyPassShortUrl,
    loyaltyPassUrlLoading: rewardsLoading || loyaltyPassUrlLoading,
    loyaltyPassUrlError,
  };
};
